import React, { useContext, useEffect, useMemo, useState } from "react";
import "./Home.css";
import { handleHistoricoFechamento } from "./functions";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";

import { AiOutlineReload } from "react-icons/ai";
import { FiActivity, FiBriefcase, FiDollarSign, FiPieChart, FiTrendingUp } from "react-icons/fi";
import { mascaraBRL, mascaraUSD } from "../../../functions/MascarasMonetarias";
import { ServicoFinanceiroClienteContext, usuarioServicoFinanceiroContextType } from "../../../context/servicoFinanceiroCliente";
import useWindowDimensions from "../../../functions/UseWindowDimensions";
import { GraficoPizza } from "../../../components/Graficos/GraficoPizza";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, PluginOptionsByType } from "chart.js";
import { _DeepPartialObject } from "chart.js/types/utils";
import { GraficoContainer } from "../../../components/Graficos/GraficoContainer";

export interface DadosPosicao {
    "cotas": number,
    "valorCota": number,
    "saldoCliente": number,
    "performanceFundo": number,
    "rendimentoConta": number,
    "totalSaques": number,
    "totalAportes": number,
    "saldoAnterior": number,
    "valorCotaAnterior": number,
    "saldoClienteRentabilidade": number,
    "valorPrimeiroAporte": number,
}

interface MetricCardProps {
    label: string;
    value: string;
    detail?: string;
    icon: React.ReactNode;
    tone?: "orange" | "cyan" | "neutral";
}

const MetricCard = ({ label, value, detail, icon, tone = "neutral" }: MetricCardProps) => (
    <article className={`cliente-home-metric ${tone}`}>
        <div className="cliente-home-metric-icon" aria-hidden="true">
            {icon}
        </div>
        <div>
            <p>{label}</p>
            <strong>{value}</strong>
            {detail && <span>{detail}</span>}
        </div>
    </article>
);

const EmptyState = ({
    message,
    onReload,
}: {
    message: string;
    onReload: () => void;
}) => (
    <div className="cliente-home-empty-state">
        <p>{message}</p>
        <button className="cliente-home-button secondary" type="button" onClick={onReload}>
            <AiOutlineReload />
            Atualizar
        </button>
    </div>
);

ChartJS.register(ArcElement, Tooltip, Legend);

function Home() {
    const [modalDesconectar, setModalDesconectar] = useState<boolean>(false);

    const [dadosPosicao, setDadosPosicao] = useState<DadosPosicao>();
    const [loadingPosicao, setLoadingPosicao] = useState<boolean>(true);
    const [errorPosicao, setErrorPosicao] = useState<boolean>(false);

    const [dataInicial] = useState<string>(new Date(new Date().setMonth(new Date().getMonth() - 72)).toISOString());
    const [dataFinal] = useState<string>(new Date().toISOString());

    const [rendimentosDia, setRendimentosDia] = useState<Array<number>>([]);
    const [valoresTotais, setValoresTotais] = useState<Array<number>>([]);
    const [valoresTotaisPrivate, setValoresTotaisPrivate] = useState<Array<number>>([]);
    const [valoresAcumulados, setValoresAcumulados] = useState<Array<number>>([]);
    const [labels, setLabels] = useState<Array<string>>([]);
    const [indexData, setIndexData] = useState<number>(0);
    const [indexDataPrivate, setIndexDataPrivate] = useState<number>(0);

    const [colorArray, setColorArray] = useState<Array<string>>([]);
    const [porcentagem, setPorcentagem] = useState<Array<number>>([]);
    const [, setValorTotal] = useState<Array<number>>([]);
    const [, setValorUnitario] = useState<Array<number>>([]);
    const [labelsMoeda, setLabelsMoeda] = useState<Array<string>>([]);

    const [loadingGrafico, setLoadingGrafico] = useState<boolean>(true);
    const [errorGrafico, setErrorGrafico] = useState<boolean>(false);

    const { height, width } = useWindowDimensions();
    const { reloadServicoFinanceiro, usuarioServicoFinanceiro } = useContext<usuarioServicoFinanceiroContextType>(ServicoFinanceiroClienteContext);

    const serviceId = usuarioServicoFinanceiro.idServicoFinanceiro;
    const serviceName = usuarioServicoFinanceiro.servicoFinanceiro || "Serviço financeiro";
    const isUSD = serviceId === 3;
    const isPrivate = [2, 3].includes(serviceId);

    const handleReload = () => {
        handleHistoricoFechamento(
            setLoadingGrafico,
            setErrorGrafico,
            dataInicial,
            dataFinal,
            setModalDesconectar,
            setRendimentosDia,
            setValoresTotais,
            setLabels,
            setValoresAcumulados,
            usuarioServicoFinanceiro,
            setLoadingPosicao,
            setErrorPosicao,
            setDadosPosicao,
            setLabelsMoeda,
            setPorcentagem,
            setValorUnitario,
            setValorTotal,
            colorArray,
            setColorArray,
            setValoresTotaisPrivate
        );
    };

    useEffect(() => {
        setValoresAcumulados([]);
        setRendimentosDia([]);
        setValoresTotais([]);
        setLabels([]);
        handleReload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataInicial, dataFinal, reloadServicoFinanceiro]);

    const formatCurrency = (value?: number) => {
        if (typeof value !== "number" || Number.isNaN(value)) {
            return isUSD ? "$ 0.00" : "R$ 0,00";
        }

        return isUSD
            ? `$ ${mascaraUSD(value.toFixed(2))}`
            : `R$ ${mascaraBRL(value.toFixed(2))}`;
    };

    const formatBRL = (value?: number) => {
        if (typeof value !== "number" || Number.isNaN(value)) {
            return "R$ 0,00";
        }

        return `R$ ${mascaraBRL(value.toFixed(2))}`;
    };

    const rentabilidade = useMemo(() => {
        if (!dadosPosicao?.totalAportes) {
            return "0%";
        }

        const percentual = ((((dadosPosicao.saldoCliente + (dadosPosicao.totalSaques * -1)) / dadosPosicao.totalAportes) - 1) * 100);
        return `${mascaraBRL(percentual.toFixed(2))}%`;
    }, [dadosPosicao]);

    const updatedAt = useMemo(() => new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(new Date()), []);

    const plugins: _DeepPartialObject<PluginOptionsByType<"pie">> = {
        legend: {
            display: true,
            position: height >= width ? "top" : "left"
        },
        tooltip: {
            enabled: true,
            mode: "point",
            callbacks: {
                title: (data) => {
                    return data.map((data2) => data2.label);
                },
                label: function (data) {
                    return [`Participação na carteira: ${porcentagem[data.dataIndex].toFixed(2)}%`];
                }
            }
        },
    };

    const positionMetrics = [
        {
            label: "Saldo líquido",
            value: formatCurrency(dadosPosicao?.saldoCliente),
            detail: "Valor consolidado da conta",
            icon: <FiDollarSign />,
            tone: "orange" as const,
        },
        {
            label: "Rentabilidade",
            value: rentabilidade,
            detail: "Resultado acumulado",
            icon: <FiTrendingUp />,
            tone: "cyan" as const,
        },
        {
            label: "Total de aportes",
            value: formatCurrency(dadosPosicao?.totalAportes),
            detail: "Capital alocado",
            icon: <FiBriefcase />,
        },
        {
            label: "Total de saques",
            value: formatCurrency(dadosPosicao?.totalSaques),
            detail: "Retiradas realizadas",
            icon: <FiActivity />,
        },
    ];

    const quotaMetrics = [
        {
            label: "Cotas",
            value: dadosPosicao?.cotas.toString().match(/^-?\d+(?:\.\d{0,4})?/)?.[0] || "0",
            detail: "Quantidade em carteira",
            icon: <FiPieChart />,
        },
        {
            label: "Valor da cota",
            value: formatBRL(dadosPosicao?.valorCota),
            detail: "Referência atual",
            icon: <FiActivity />,
        },
    ];

    return (
        <>
            <Navbar />
            <main id="container" className="cliente-home-page">
                <section className="cliente-home-hero" aria-labelledby="cliente-home-title">
                    <div>
                        <p className="cliente-home-eyebrow">Omex Capital</p>
                        <h1 id="cliente-home-title">Visão Geral</h1>
                        <p className="cliente-home-hero-copy">
                            Acompanhe posição consolidada, evolução de performance e composição dos ativos em um painel direto.
                        </p>
                    </div>

                    <div className="cliente-home-hero-aside">
                        <span>Serviço selecionado</span>
                        <strong>{serviceName}</strong>
                        <p>Atualizado em {updatedAt}</p>
                        <button className="cliente-home-button" type="button" onClick={handleReload}>
                            <AiOutlineReload />
                            Atualizar dados
                        </button>
                    </div>
                </section>

                <section className="cliente-home-panel" aria-labelledby="cliente-position-title">
                    <div className="cliente-home-section-header">
                        <div>
                            <p>Resumo financeiro</p>
                            <h2 id="cliente-position-title">Posição consolidada</h2>
                        </div>
                        <span>{isUSD ? "USD" : "BRL"}</span>
                    </div>

                    {errorPosicao ? (
                        <EmptyState
                            message="Não foi possível carregar a posição consolidada agora."
                            onReload={handleReload}
                        />
                    ) : loadingPosicao ? (
                        <div className="cliente-home-loading">
                            <LoadingButton />
                        </div>
                    ) : (
                        <div className="cliente-home-metric-grid">
                            {!isUSD && quotaMetrics.map((metric) => (
                                <MetricCard key={metric.label} {...metric} />
                            ))}
                            {positionMetrics.map((metric) => (
                                <MetricCard key={metric.label} {...metric} />
                            ))}
                        </div>
                    )}
                </section>

                {serviceId !== 3 && (
                    <section className="cliente-home-panel cliente-home-rules" aria-labelledby="cliente-rules-title">
                        <div className="cliente-home-section-header">
                            <div>
                                <p>Regras operacionais</p>
                                <h2 id="cliente-rules-title">Informações gerais</h2>
                            </div>
                        </div>

                        <div className="cliente-home-rules-grid">
                            <article>
                                <span>Cota de aplicação</span>
                                <strong>D+1</strong>
                            </article>
                            <article>
                                <span>Cota de resgate</span>
                                <strong>D+2</strong>
                            </article>
                            <article>
                                <span>Pagamento do resgate</span>
                                <strong>D+5</strong>
                                <small>Após cotização</small>
                            </article>
                            <article>
                                <span>Aplicação inicial mínima</span>
                                <strong>R$ 25.000,00</strong>
                            </article>
                        </div>
                    </section>
                )}

                <section className="cliente-home-chart-stack" aria-label="Gráficos de performance">
                    {!isPrivate ? (
                        <>
                            <GraficoContainer
                                loading={loadingGrafico}
                                erro={errorGrafico}
                                titulo="Performance mensal da assessoria Omex Capital"
                                dados={valoresTotais}
                                labels={labels}
                                indexData={indexData}
                                setIndexData={setIndexData}
                                tituloTooltip="Diferença percentual mensal"
                                dadoTooltip={`Valor percentual de diferença: ${valoresTotais[indexData]?.toFixed(2)}%`}
                                onReload={handleReload}
                                graficoPorcentagem={true}
                            />
                            <GraficoContainer
                                loading={loadingGrafico}
                                erro={errorGrafico}
                                titulo="Rendimento acumulado da assessoria Omex Capital"
                                dados={valoresAcumulados}
                                labels={labels}
                                indexData={indexDataPrivate}
                                setIndexData={setIndexDataPrivate}
                                tituloTooltip="Valor acumulado percentual"
                                dadoTooltip={`Valor acumulado: ${valoresAcumulados[indexDataPrivate]?.toFixed(2)}%`}
                                onReload={handleReload}
                                graficoPorcentagem={true}
                            />
                        </>
                    ) : (
                        <>
                            <GraficoContainer
                                loading={loadingGrafico}
                                erro={errorGrafico}
                                titulo="Performance mensal Private Omex Capital"
                                dados={valoresTotaisPrivate}
                                labels={labels}
                                indexData={indexData}
                                setIndexData={setIndexData}
                                tituloTooltip="Diferença percentual mensal"
                                dadoTooltip={`Valor percentual de diferença: ${valoresTotaisPrivate[indexData]?.toFixed(2)}%`}
                                onReload={handleReload}
                                graficoPorcentagem={true}
                            />
                            <GraficoContainer
                                loading={loadingGrafico}
                                erro={errorGrafico}
                                titulo="Percentual acumulado Private Omex Capital"
                                dados={rendimentosDia || [0]}
                                labels={labels}
                                indexData={indexDataPrivate}
                                setIndexData={setIndexDataPrivate}
                                tituloTooltip="Percentual acumulado"
                                dadoTooltip={`Percentual acumulado: % ${mascaraUSD(rendimentosDia?.[indexDataPrivate]?.toFixed(2))}`}
                                onReload={handleReload}
                                graficoPorcentagem={true}
                            />
                        </>
                    )}
                </section>

                <section className="cliente-home-panel cliente-home-pie-panel" aria-labelledby="cliente-pie-title">
                    <div className="cliente-home-section-header">
                        <div>
                            <p>Alocação</p>
                            <h2 id="cliente-pie-title">Composição da carteira</h2>
                        </div>
                    </div>

                    {errorGrafico ? (
                        <EmptyState
                            message="Não foi possível carregar a composição da carteira."
                            onReload={handleReload}
                        />
                    ) : loadingGrafico ? (
                        <div className="cliente-home-loading">
                            <LoadingButton />
                        </div>
                    ) : (
                        <div className="divGrafico">
                            <GraficoPizza
                                colorArray={colorArray}
                                labels={labelsMoeda}
                                porcentagens={porcentagem}
                                plugins={plugins}
                                tituloGrafico="Composição da carteira"
                                subTituloGrafico="Moedas com menos de 1% de participação foram ocultadas"
                                mobile={height >= width}
                                exibirCabecalho={false}
                            />
                        </div>
                    )}
                </section>
            </main>
            <Footer />
            {modalDesconectar && <ModalDesconectar modal={modalDesconectar} />}
        </>
    );
};

export default Home;
